import type { ListStackableCasesResponse, StackableCase } from '@compass/client/types/call';
import { CallStackRejectionReason } from '@compass/client/types/call';

const REJECTION_LABELS: Record<number, string> = {
  [CallStackRejectionReason.CALL_STACK_REJECTION_REASON_NOT_FOUND]: 'Case not found',
  [CallStackRejectionReason.CALL_STACK_REJECTION_REASON_NOT_ASSIGNED]: 'Just reassigned',
  [CallStackRejectionReason.CALL_STACK_REJECTION_REASON_STATE_CHANGED]:
    'No longer waiting on a call',
  [CallStackRejectionReason.CALL_STACK_REJECTION_REASON_SAME_CASE]:
    'This is the case you are calling from',
  [CallStackRejectionReason.CALL_STACK_REJECTION_REASON_PAYER_MISMATCH]: 'Different payer',
  [CallStackRejectionReason.CALL_STACK_REJECTION_REASON_ON_ANOTHER_CALL]: 'Already on another call',
  [CallStackRejectionReason.CALL_STACK_REJECTION_REASON_CALL_ENDED]: 'Call has ended',
  [CallStackRejectionReason.CALL_STACK_REJECTION_REASON_CALL_NOT_CONNECTED]:
    "Call hasn't connected yet",
  [CallStackRejectionReason.CALL_STACK_REJECTION_REASON_WORKFLOW_MISMATCH]: 'Different workflow',
};

/**
 * Turns a server rejection reason into words an agent understands, shown
 * inline on the offending row.
 */
export function rejectionLabel(reason: CallStackRejectionReason): string {
  return REJECTION_LABELS[reason] ?? 'Cannot be stacked';
}

type SlaFields = Pick<StackableCase, 'has_sla' | 'sla_remaining_seconds' | 'sla_breached'>;

/** Under this much SLA left, a card is flagged as due soon. */
const SLA_SOON_SECONDS = 2 * 60 * 60;

/** "<1m", "45m", "2h 10m", or from a day on "198d 19h" (minutes dropped). */
function formatDuration(seconds: number): string {
  const totalMinutes = Math.floor(seconds / 60);
  if (totalMinutes < 1) return '<1m';
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return hours === 0 ? `${days}d` : `${days}d ${hours}h`;
  if (hours === 0) return `${minutes}m`;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}

/**
 * The card's SLA pill: "SLA breached 2h 10m ago", "SLA breaching in 1h 35m"
 * (under two hours left), "SLA in 5h", or "No SLA".
 */
export function formatSla(c: SlaFields): string {
  const remaining = c.sla_remaining_seconds ?? 0;
  switch (slaTone(c)) {
    case 'breached':
      return c.has_sla && remaining < 0
        ? `SLA breached ${formatDuration(-remaining)} ago`
        : 'SLA breached';
    case 'soon':
      return `SLA breaching in ${formatDuration(remaining)}`;
    default:
      return c.has_sla ? `SLA in ${formatDuration(remaining)}` : 'No SLA';
  }
}

export function slaTone(c: SlaFields): 'breached' | 'soon' | 'ok' {
  const remaining = c.sla_remaining_seconds ?? 0;
  if (c.sla_breached || (c.has_sla && remaining < 0)) return 'breached';
  if (c.has_sla && remaining < SLA_SOON_SECONDS) return 'soon';
  return 'ok';
}

/** A case's short label: "#" and the last six characters of its id. */
export function shortCaseId(caseRecordId: string): string {
  return `#${caseRecordId.slice(-6)}`;
}

/** The Stack call tab's accessible name, with what is left to stack. */
export function stackTabLabel(left: number): string {
  if (left <= 0) return 'Stack call';
  return `Stack call, ${left} ${left === 1 ? 'case' : 'cases'} left to stack`;
}

/** The cached list after a case was stacked: locked in place, one fewer left. */
export function markStacked(
  data: ListStackableCasesResponse | undefined,
  caseRecordId: string,
): ListStackableCasesResponse | undefined {
  if (!data) return data;
  return {
    ...data,
    cases: data.cases?.map(c =>
      c.case_record_id === caseRecordId ? { ...c, in_this_stack: true } : c,
    ),
    stackable_count: Math.max(0, (data.stackable_count ?? 0) - 1),
  };
}

/** The cached list after the server refused a case: gone, one fewer left. */
export function dropCase(
  data: ListStackableCasesResponse | undefined,
  caseRecordId: string,
): ListStackableCasesResponse | undefined {
  if (!data) return data;
  return {
    ...data,
    cases: data.cases?.filter(c => c.case_record_id !== caseRecordId),
    stackable_count: Math.max(0, (data.stackable_count ?? 0) - 1),
  };
}
